import { httpsCallable } from 'firebase/functions';
import { useCallback, useState } from 'react';
import {
  fromUnknownError,
  PermissionError,
  RateLimitError,
  type AppError,
} from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * `aiAnalyzePost` callable wrapper hook for admin (旧 v1 admin の reanalyzePost / 新 v2 では
 * user app の `useAiAnalyzePost` と同 callable を共有)。
 *
 * 旧 admin `PostsModerationPage.tsx` L150-212 の `reanalyzeSingle` /
 * `reanalyzeAllFailed` / `reanalyzeNeedsAttention` UX を v2 admin にも復元するため、
 * apps/user の useAiAnalyzePost と同一実装を admin 側にも配置 (apps/* 間 deep import は禁止)。
 *
 * 返値 `result`:
 *   - `{ ok: true }`: 解析完了 (aiAnalysis は post doc に書込済、onSnapshot で post 取得し直すこと)
 *   - `{ ok: false, error: 'GEMINI_API_KEY not configured' }`: API key 未設定 (dev/emulator)
 *   - throw: HttpsError (rate limit / 認証 / その他)
 */

/** 日本語 (かな・漢字) を含むか。Functions が返した理由をそのまま見せてよいかの判定。 */
const JAPANESE_RE = /[\u3040-\u30ff\u3400-\u9fff]/;

export type AiAnalyzePostResult = { ok: true } | { ok: false; error: string };

export type AiAnalyzePostState =
  | { status: 'idle'; error: null }
  | { status: 'pending'; error: null }
  | { status: 'success'; result: AiAnalyzePostResult; error: null }
  | { status: 'error'; error: AppError };

export function useAiAnalyzePost(): {
  state: AiAnalyzePostState;
  /** surveyKey = 解析の観点 (docs/29)。未指定・null は既定の観点。 */
  analyze: (
    districtId: string,
    postId: string,
    force?: boolean,
    surveyKey?: string | null,
  ) => Promise<AiAnalyzePostResult>;
  reset: () => void;
} {
  const [state, setState] = useState<AiAnalyzePostState>({ status: 'idle', error: null });

  const analyze = useCallback(
    async (districtId: string, postId: string, force = false, surveyKey: string | null = null) => {
      setState({ status: 'pending', error: null });
      try {
        const fn = httpsCallable<
          { districtId: string; postId: string; force?: boolean; surveyKey?: string },
          AiAnalyzePostResult
        >(functions, 'aiAnalyzePost');
        const res = await fn({ districtId, postId, force, ...(surveyKey ? { surveyKey } : {}) });
        setState({ status: 'success', result: res.data, error: null });
        return res.data;
      } catch (e) {
        let err = fromUnknownError(e);
        // 権限なし (permission-denied)・上限 (resource-exhausted) は Functions が日本語の理由を
        // message に入れて返すので、それを残す。英語・コードだけのときに限り決まった文に置き換える。
        const serverJa = JAPANESE_RE.test(err.message);
        if (err instanceof RateLimitError && !serverJa) {
          err = new RateLimitError(
            err.code,
            'AI 解析の呼出上限に達しました。しばらく経ってから再試行してください。',
            { cause: e, details: err.details },
          );
        } else if (err instanceof PermissionError && !serverJa) {
          err = new PermissionError(err.code, 'この投稿の AI 解析を実行する権限がありません。', {
            cause: e,
            details: err.details,
          });
        }
        setState({ status: 'error', error: err });
        throw err;
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setState({ status: 'idle', error: null });
  }, []);

  return { state, analyze, reset };
}
